import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChecksBanner, { ChecksBannerItem, LinkButton } from './ChecksBanner.tsx';
import { FormField, getItemDisplayIndex } from './useDocumentConfig.ts';
import { useScrollToItem } from './useScrollToItem.ts';
import { collectValidationChecks, ItemRef, summarizeValidationChecks } from './validationChecks.ts';

const MAX_VISIBLE_SUMMARIES = 3;

interface ManualReviewBannerProps {
    document: any;
    itemFormFieldGroups: Record<string, FormField[][]>;
}

const ManualReviewBanner = ({ document, itemFormFieldGroups }: ManualReviewBannerProps) => {
    const { t } = useTranslation('assistance');
    const scrollToItem = useScrollToItem(itemFormFieldGroups);
    const [isExpanded, setIsExpanded] = useState(false);

    const summaries = useMemo(
        () => summarizeValidationChecks(t, collectValidationChecks(document, Object.keys(itemFormFieldGroups))),
        [document, itemFormFieldGroups, t]
    );

    if (summaries.length === 0) return null;

    const pinnedCount = summaries.filter(({ hasDocumentCheck }) => hasDocumentCheck).length;
    const collapsedSummaries = summaries.slice(0, Math.max(MAX_VISIBLE_SUMMARIES, pinnedCount));
    const hiddenIssueCount = summaries.length - collapsedSummaries.length;
    const visibleSummaries = isExpanded ? summaries : collapsedSummaries;

    const getItemLabel = ({ itemType, itemIndex }: ItemRef) =>
        `#${getItemDisplayIndex(itemIndex, itemFormFieldGroups[itemType]?.[itemIndex]?.[0]?.displayItemIndex)}`;

    return (
        <ChecksBanner
            severity="warning"
            title={t('validationChecks.manualReviewRequired')}
            itemCount={visibleSummaries.length}
            listClassName="whitespace-pre-line"
            footer={
                hiddenIssueCount > 0 && (
                    <LinkButton
                        className="ml-6 mt-2"
                        aria-expanded={isExpanded}
                        onClick={() => setIsExpanded((expanded) => !expanded)}
                    >
                        {isExpanded
                            ? t('validationChecks.showLess')
                            : t('validationChecks.showMore', { count: hiddenIssueCount })}
                    </LinkButton>
                )
            }
        >
            {visibleSummaries.map(({ message, items }) => (
                <ChecksBannerItem key={message} message={message}>
                    {items.length > 0 && (
                        <div>
                            {items.map((item, index) => (
                                <React.Fragment key={`${item.itemType}:${item.itemIndex}`}>
                                    {index > 0 && ', '}
                                    <LinkButton onClick={() => scrollToItem(item)}>{getItemLabel(item)}</LinkButton>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </ChecksBannerItem>
            ))}
        </ChecksBanner>
    );
};

export default ManualReviewBanner;
